import { __ } from '@wordpress/i18n';
import { Address } from '../types';

export const STATE_INCLUDED_COUNTRIES = ['AU', 'BR', 'CA', 'CH', 'ES', 'HK', 'IE', 'IN', 'IT', 'JP', 'MY', 'MX', 'US'];
export const POSTAL_CODE_EXCLUDED_COUNTRIES = ['HK'];
export const CITY_EXCLUDED_COUNTRIES = ['SG'];
import countries from './countries.json';

export const hasPostal = (countryCode: string) => {
  return !POSTAL_CODE_EXCLUDED_COUNTRIES.includes(countryCode);
};

export const hasCity = (countryCode: string) => {
  return !CITY_EXCLUDED_COUNTRIES.includes(countryCode);
};

export const hasState = (countryCode: string) => {
  return STATE_INCLUDED_COUNTRIES.includes(countryCode);
};

export const hasCompleteAddress = args => {
  const { country, state } = args;
  // does it have the fields filled out?
  if (!hasRequiredFields(args)) {
    return false;
  }
  return hasState(country) ? hasCorrectState(country, state) : true;
};

export const hasCorrectState = (country, state) => {
  return (countries?.[country] || ([] as Array<{ value: string; label: string }>)).some(({ value }) => value === state);
};

export const hasRequiredFields = ({ city, country, line_1, postal_code, state }) => {
  if (!country) {
    return false;
  }
  if (hasPostal(country) && !postal_code) {
    return false;
  }
  if (hasCity(country) && !city) {
    return false;
  }
  if (hasState(country) && !state) {
    return false;
  }
  return !!line_1;
};

export const countryChoices: Array<{ value: string; label: string }> = [
  { value: 'AF', label: __('Afghanistan', 'surecart') },
  { value: 'AL', label: __('Albania', 'surecart') },
  { value: 'DZ', label: __('Algeria', 'surecart') },
  { value: 'AS', label: __('American Samoa', 'surecart') },
  { value: 'AD', label: __('Andorra', 'surecart') },
  { value: 'AO', label: __('Angola', 'surecart') },
  { value: 'AI', label: __('Anguilla', 'surecart') },
  { value: 'AQ', label: __('Antarctica', 'surecart') },
  { value: 'AG', label: __('Antigua and Barbuda', 'surecart') },
  { value: 'AR', label: __('Argentina', 'surecart') },
  { value: 'AM', label: __('Armenia', 'surecart') },
  { value: 'AW', label: __('Aruba', 'surecart') },
  { value: 'AU', label: __('Australia', 'surecart') },
  { value: 'AT', label: __('Austria', 'surecart') },
  { value: 'AZ', label: __('Azerbaijan', 'surecart') },
  { value: 'BS', label: __('Bahamas', 'surecart') },
  { value: 'BH', label: __('Bahrain', 'surecart') },
  { value: 'BD', label: __('Bangladesh', 'surecart') },
  { value: 'BB', label: __('Barbados', 'surecart') },
  { value: 'BY', label: __('Belarus', 'surecart') },
  { value: 'BE', label: __('Belgium', 'surecart') },
  { value: 'BZ', label: __('Belize', 'surecart') },
  { value: 'BJ', label: __('Benin', 'surecart') },
  { value: 'BM', label: __('Bermuda', 'surecart') },
  { value: 'BT', label: __('Bhutan', 'surecart') },
  { value: 'BO', label: __('Bolivia', 'surecart') },
  { value: 'BQ', label: __('Bonaire, Sint Eustatius and Saba', 'surecart') },
  { value: 'BA', label: __('Bosnia and Herzegovina', 'surecart') },
  { value: 'BW', label: __('Botswana', 'surecart') },
  { value: 'BV', label: __('Bouvet Island', 'surecart') },
  { value: 'BR', label: __('Brazil', 'surecart') },
  { value: 'IO', label: __('British Indian Ocean Territory', 'surecart') },
  { value: 'BN', label: __('Brunei Darussalam', 'surecart') },
  { value: 'BG', label: __('Bulgaria', 'surecart') },
  { value: 'BF', label: __('Burkina Faso', 'surecart') },
  { value: 'BI', label: __('Burundi', 'surecart') },
  { value: 'CV', label: __('Cabo Verde', 'surecart') },
  { value: 'KH', label: __('Cambodia', 'surecart') },
  { value: 'CM', label: __('Cameroon', 'surecart') },
  { value: 'CA', label: __('Canada', 'surecart') },
  { value: 'KY', label: __('Cayman Islands', 'surecart') },
  { value: 'CF', label: __('Central African Republic', 'surecart') },
  { value: 'TD', label: __('Chad', 'surecart') },
  { value: 'CL', label: __('Chile', 'surecart') },
  { value: 'CN', label: __('China', 'surecart') },
  { value: 'CX', label: __('Christmas Island', 'surecart') },
  { value: 'CC', label: __('Cocos (Keeling) Islands', 'surecart') },
  { value: 'CO', label: __('Colombia', 'surecart') },
  { value: 'KM', label: __('Comoros', 'surecart') },
  { value: 'CG', label: __('Congo', 'surecart') },
  { value: 'CD', label: __('Congo, The Democratic Republic of the', 'surecart') },
  { value: 'CK', label: __('Cook Islands', 'surecart') },
  { value: 'CR', label: __('Costa Rica', 'surecart') },
  { value: 'HR', label: __('Croatia', 'surecart') },
  { value: 'CU', label: __('Cuba', 'surecart') },
  { value: 'CW', label: __('Curaçao', 'surecart') },
  { value: 'CY', label: __('Cyprus', 'surecart') },
  { value: 'CZ', label: __('Czechia', 'surecart') },
  { value: 'CI', label: __("Côte d'Ivoire", 'surecart') },
  { value: 'DK', label: __('Denmark', 'surecart') },
  { value: 'DJ', label: __('Djibouti', 'surecart') },
  { value: 'DM', label: __('Dominica', 'surecart') },
  { value: 'DO', label: __('Dominican Republic', 'surecart') },
  { value: 'EC', label: __('Ecuador', 'surecart') },
  { value: 'EG', label: __('Egypt', 'surecart') },
  { value: 'SV', label: __('El Salvador', 'surecart') },
  { value: 'GQ', label: __('Equatorial Guinea', 'surecart') },
  { value: 'ER', label: __('Eritrea', 'surecart') },
  { value: 'EE', label: __('Estonia', 'surecart') },
  { value: 'SZ', label: __('Eswatini', 'surecart') },
  { value: 'ET', label: __('Ethiopia', 'surecart') },
  { value: 'FK', label: __('Falkland Islands (Malvinas)', 'surecart') },
  { value: 'FO', label: __('Faroe Islands', 'surecart') },
  { value: 'FJ', label: __('Fiji', 'surecart') },
  { value: 'FI', label: __('Finland', 'surecart') },
  { value: 'FR', label: __('France', 'surecart') },
  { value: 'GF', label: __('French Guiana', 'surecart') },
  { value: 'PF', label: __('French Polynesia', 'surecart') },
  { value: 'TF', label: __('French Southern Territories', 'surecart') },
  { value: 'GA', label: __('Gabon', 'surecart') },
  { value: 'GM', label: __('Gambia', 'surecart') },
  { value: 'GE', label: __('Georgia', 'surecart') },
  { value: 'DE', label: __('Germany', 'surecart') },
  { value: 'GH', label: __('Ghana', 'surecart') },
  { value: 'GI', label: __('Gibraltar', 'surecart') },
  { value: 'GR', label: __('Greece', 'surecart') },
  { value: 'GL', label: __('Greenland', 'surecart') },
  { value: 'GD', label: __('Grenada', 'surecart') },
  { value: 'GP', label: __('Guadeloupe', 'surecart') },
  { value: 'GT', label: __('Guatemala', 'surecart') },
  { value: 'GG', label: __('Guernsey', 'surecart') },
  { value: 'GN', label: __('Guinea', 'surecart') },
  { value: 'GW', label: __('Guinea-Bissau', 'surecart') },
  { value: 'GY', label: __('Guyana', 'surecart') },
  { value: 'HT', label: __('Haiti', 'surecart') },
  { value: 'HM', label: __('Heard Island and McDonald Islands', 'surecart') },
  { value: 'VA', label: __('Holy See (Vatican City State)', 'surecart') },
  { value: 'HN', label: __('Honduras', 'surecart') },
  { value: 'HK', label: __('Hong Kong', 'surecart') },
  { value: 'HU', label: __('Hungary', 'surecart') },
  { value: 'IS', label: __('Iceland', 'surecart') },
  { value: 'IN', label: __('India', 'surecart') },
  { value: 'ID', label: __('Indonesia', 'surecart') },
  { value: 'IR', label: __('Iran, Islamic Republic of', 'surecart') },
  { value: 'IQ', label: __('Iraq', 'surecart') },
  { value: 'IE', label: __('Ireland', 'surecart') },
  { value: 'IM', label: __('Isle of Man', 'surecart') },
  { value: 'IL', label: __('Israel', 'surecart') },
  { value: 'IT', label: __('Italy', 'surecart') },
  { value: 'JM', label: __('Jamaica', 'surecart') },
  { value: 'JP', label: __('Japan', 'surecart') },
  { value: 'JE', label: __('Jersey', 'surecart') },
  { value: 'JO', label: __('Jordan', 'surecart') },
  { value: 'KZ', label: __('Kazakhstan', 'surecart') },
  { value: 'KE', label: __('Kenya', 'surecart') },
  { value: 'KI', label: __('Kiribati', 'surecart') },
  { value: 'KW', label: __('Kuwait', 'surecart') },
  { value: 'KG', label: __('Kyrgyzstan', 'surecart') },
  { value: 'LA', label: __("Lao People's Democratic Republic", 'surecart') },
  { value: 'LV', label: __('Latvia', 'surecart') },
  { value: 'LB', label: __('Lebanon', 'surecart') },
  { value: 'LS', label: __('Lesotho', 'surecart') },
  { value: 'LR', label: __('Liberia', 'surecart') },
  { value: 'LY', label: __('Libya', 'surecart') },
  { value: 'LI', label: __('Liechtenstein', 'surecart') },
  { value: 'LT', label: __('Lithuania', 'surecart') },
  { value: 'LU', label: __('Luxembourg', 'surecart') },
  { value: 'MO', label: __('Macao', 'surecart') },
  { value: 'MG', label: __('Madagascar', 'surecart') },
  { value: 'MW', label: __('Malawi', 'surecart') },
  { value: 'MY', label: __('Malaysia', 'surecart') },
  { value: 'MV', label: __('Maldives', 'surecart') },
  { value: 'ML', label: __('Mali', 'surecart') },
  { value: 'MT', label: __('Malta', 'surecart') },
  { value: 'MH', label: __('Marshall Islands', 'surecart') },
  { value: 'MQ', label: __('Martinique', 'surecart') },
  { value: 'MR', label: __('Mauritania', 'surecart') },
  { value: 'MU', label: __('Mauritius', 'surecart') },
  { value: 'YT', label: __('Mayotte', 'surecart') },
  { value: 'MX', label: __('Mexico', 'surecart') },
  { value: 'FM', label: __('Micronesia, Federated States of', 'surecart') },
  { value: 'MD', label: __('Moldova', 'surecart') },
  { value: 'MC', label: __('Monaco', 'surecart') },
  { value: 'MN', label: __('Mongolia', 'surecart') },
  { value: 'ME', label: __('Montenegro', 'surecart') },
  { value: 'MS', label: __('Montserrat', 'surecart') },
  { value: 'MA', label: __('Morocco', 'surecart') },
  { value: 'MZ', label: __('Mozambique', 'surecart') },
  { value: 'MM', label: __('Myanmar', 'surecart') },
  { value: 'NA', label: __('Namibia', 'surecart') },
  { value: 'NR', label: __('Nauru', 'surecart') },
  { value: 'NP', label: __('Nepal', 'surecart') },
  { value: 'NL', label: __('Netherlands', 'surecart') },
  { value: 'NC', label: __('New Caledonia', 'surecart') },
  { value: 'NZ', label: __('New Zealand', 'surecart') },
  { value: 'NI', label: __('Nicaragua', 'surecart') },
  { value: 'NE', label: __('Niger', 'surecart') },
  { value: 'NG', label: __('Nigeria', 'surecart') },
  { value: 'NU', label: __('Niue', 'surecart') },
  { value: 'NF', label: __('Norfolk Island', 'surecart') },
  { value: 'KP', label: __('North Korea', 'surecart') },
  { value: 'MK', label: __('North Macedonia', 'surecart') },
  { value: 'MP', label: __('Northern Mariana Islands', 'surecart') },
  { value: 'NO', label: __('Norway', 'surecart') },
  { value: 'OM', label: __('Oman', 'surecart') },
  { value: 'PK', label: __('Pakistan', 'surecart') },
  { value: 'PW', label: __('Palau', 'surecart') },
  { value: 'PS', label: __('Palestine, State of', 'surecart') },
  { value: 'PA', label: __('Panama', 'surecart') },
  { value: 'PG', label: __('Papua New Guinea', 'surecart') },
  { value: 'PY', label: __('Paraguay', 'surecart') },
  { value: 'PE', label: __('Peru', 'surecart') },
  { value: 'PH', label: __('Philippines', 'surecart') },
  { value: 'PN', label: __('Pitcairn', 'surecart') },
  { value: 'PL', label: __('Poland', 'surecart') },
  { value: 'PT', label: __('Portugal', 'surecart') },
  { value: 'QA', label: __('Qatar', 'surecart') },
  { value: 'RO', label: __('Romania', 'surecart') },
  { value: 'RU', label: __('Russian Federation', 'surecart') },
  { value: 'RW', label: __('Rwanda', 'surecart') },
  { value: 'RE', label: __('Réunion', 'surecart') },
  { value: 'BL', label: __('Saint Barthélemy', 'surecart') },
  { value: 'SH', label: __('Saint Helena, Ascension and Tristan da Cunha', 'surecart') },
  { value: 'KN', label: __('Saint Kitts and Nevis', 'surecart') },
  { value: 'LC', label: __('Saint Lucia', 'surecart') },
  { value: 'MF', label: __('Saint Martin (French part)', 'surecart') },
  { value: 'PM', label: __('Saint Pierre and Miquelon', 'surecart') },
  { value: 'VC', label: __('Saint Vincent and the Grenadines', 'surecart') },
  { value: 'WS', label: __('Samoa', 'surecart') },
  { value: 'SM', label: __('San Marino', 'surecart') },
  { value: 'ST', label: __('Sao Tome and Principe', 'surecart') },
  { value: 'SA', label: __('Saudi Arabia', 'surecart') },
  { value: 'SN', label: __('Senegal', 'surecart') },
  { value: 'RS', label: __('Serbia', 'surecart') },
  { value: 'SC', label: __('Seychelles', 'surecart') },
  { value: 'SL', label: __('Sierra Leone', 'surecart') },
  { value: 'SG', label: __('Singapore', 'surecart') },
  { value: 'SX', label: __('Sint Maarten (Dutch part)', 'surecart') },
  { value: 'SK', label: __('Slovakia', 'surecart') },
  { value: 'SI', label: __('Slovenia', 'surecart') },
  { value: 'SB', label: __('Solomon Islands', 'surecart') },
  { value: 'SO', label: __('Somalia', 'surecart') },
  { value: 'ZA', label: __('South Africa', 'surecart') },
  { value: 'GS', label: __('South Georgia and the South Sandwich Islands', 'surecart') },
  { value: 'KR', label: __('South Korea', 'surecart') },
  { value: 'SS', label: __('South Sudan', 'surecart') },
  { value: 'ES', label: __('Spain', 'surecart') },
  { value: 'LK', label: __('Sri Lanka', 'surecart') },
  { value: 'SD', label: __('Sudan', 'surecart') },
  { value: 'SR', label: __('Suriname', 'surecart') },
  { value: 'SJ', label: __('Svalbard and Jan Mayen', 'surecart') },
  { value: 'SE', label: __('Sweden', 'surecart') },
  { value: 'CH', label: __('Switzerland', 'surecart') },
  { value: 'SY', label: __('Syrian Arab Republic', 'surecart') },
  { value: 'TW', label: __('Taiwan', 'surecart') },
  { value: 'TJ', label: __('Tajikistan', 'surecart') },
  { value: 'TZ', label: __('Tanzania', 'surecart') },
  { value: 'TH', label: __('Thailand', 'surecart') },
  { value: 'TL', label: __('Timor-Leste', 'surecart') },
  { value: 'TG', label: __('Togo', 'surecart') },
  { value: 'TK', label: __('Tokelau', 'surecart') },
  { value: 'TO', label: __('Tonga', 'surecart') },
  { value: 'TT', label: __('Trinidad and Tobago', 'surecart') },
  { value: 'TN', label: __('Tunisia', 'surecart') },
  { value: 'TR', label: __('Turkey', 'surecart') },
  { value: 'TM', label: __('Turkmenistan', 'surecart') },
  { value: 'TC', label: __('Turks and Caicos Islands', 'surecart') },
  { value: 'TV', label: __('Tuvalu', 'surecart') },
  { value: 'UG', label: __('Uganda', 'surecart') },
  { value: 'UA', label: __('Ukraine', 'surecart') },
  { value: 'AE', label: __('United Arab Emirates', 'surecart') },
  { value: 'GB', label: __('United Kingdom', 'surecart') },
  { value: 'US', label: __('United States', 'surecart') },
  { value: 'UM', label: __('United States Minor Outlying Islands', 'surecart') },
  { value: 'UY', label: __('Uruguay', 'surecart') },
  { value: 'UZ', label: __('Uzbekistan', 'surecart') },
  { value: 'VU', label: __('Vanuatu', 'surecart') },
  { value: 'VE', label: __('Venezuela', 'surecart') },
  { value: 'VN', label: __('Vietnam', 'surecart') },
  { value: 'VG', label: __('Virgin Islands, British', 'surecart') },
  { value: 'VI', label: __('Virgin Islands, U.S.', 'surecart') },
  { value: 'WF', label: __('Wallis and Futuna', 'surecart') },
  { value: 'EH', label: __('Western Sahara', 'surecart') },
  { value: 'YE', label: __('Yemen', 'surecart') },
  { value: 'ZM', label: __('Zambia', 'surecart') },
  { value: 'ZW', label: __('Zimbabwe', 'surecart') },
  { value: 'AX', label: __('Åland Islands', 'surecart') },
];

export const isAddressComplete = (address: Partial<Address>) => {
  return (
    address?.country && // must have country
    address?.line_1 && // must have line 1
    (hasPostal(address.country) ? address?.postal_code : true) && // should have postal code if applicable
    (hasCity(address.country) ? address?.city : true) && // should have city if applicable
    (hasState(address.country) ? address?.state : true) // should have state if applicable
  );
};

export const isAddressCompleteEnough = (address: Partial<Address>) => {
  return (
    address?.country && // must have country
    (hasPostal(address.country) ? address?.postal_code : true) && // should have postal code if applicable
    (hasCity(address.country) ? address?.city : true) && // should have city if applicable
    (hasState(address.country) ? address?.state : true) // should have state if applicable
  );
};
